import {ChangeDetectorRef, Component, Inject, Input, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {DcpComponentClassifications} from '@dcp/configurator';
import {Category, Feature, FeatureValue, Product} from '@spartacus/core';
import {CategoryService} from '@app/services/category.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {Meta} from '@angular/platform-browser';

export const META_TAG_KEYWORDS = 'keywords';

@Component({
  selector: 'dcp-classifications-template',
  templateUrl: './classifications-template.component.html',
  styleUrls: ['./classifications-template.component.scss'],
})
export class ClassificationsTemplateComponent {
  @Input() parentHeroComponent: boolean;
  @Input() parentTechnicalInformationComponent: boolean;
  features: Feature[];
  classifications: Feature[];
  sapCertified: FeatureValue;
  sapCertifications: FeatureValue[];
  solutionTypes: string[];
  worksWith: string[];
  worksWithModalConfig = {
    initiator: {},
    isActive: false,
  };
  solutionTypesModalConfig = {
    initiator: {},
    isActive: false,
  };
  partnerCertificationModalConfig = {
    initiator: {},
    isActive: false,
  };
  partnerCertificationTechnicalInfoConfig = {
    initiator: {},
    isActive: false,
  };
  private innerProduct: Product;
  private innerProductClassifications: DcpComponentClassifications[];

  currentFilteredCategories: Category[] = [];

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    public categoryService: CategoryService,
    private metaService: Meta,
    private productUtilitiesService: DcpProductUtilitiesService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  get product(): Product {
    return this.innerProduct;
  }

  @Input() set product(product: Product) {
    this.innerProduct = product;
    this.prepareFeaturesAndAddTags();
    this.prepareFilteredCategories();
    this.cdr.detectChanges();
  }

  get productClassifications(): DcpComponentClassifications[] {
    return this.innerProductClassifications;
  }

  @Input() set productClassifications(productClassifications: DcpComponentClassifications[]) {
    this.innerProductClassifications = productClassifications;
    this.prepareFeaturesAndAddTags();
  }

  get hasGeneralTechnicalInformationSection(): boolean {
    return this.hasSapCertified || this.hasSolutionTypes || this.hasWorksWith || this.hasCategories;
  }

  get hasClassification(): boolean {
    return !!this.hasSolutionTypes || !!this.hasWorksWith || !!this.hasSapCertifications;
  }

  get hasSapCertifications(): boolean {
    return !!this.sapCertifications?.length;
  }

  get hasSapCertified(): boolean {
    return !!this.sapCertified?.value;
  }

  get hasSolutionTypes(): boolean {
    return !!this.solutionTypes?.length;
  }

  get hasWorksWith(): boolean {
    return !!this.worksWith?.length;
  }

  get hasCategories(): boolean {
    return !!this.product?.supcategories?.length;
  }

  prepareFilteredCategories(): void {
    const filteredCategories = [];
    this.product?.supcategories?.forEach(category => {
      if (this.categoryService.isDisplayed(category)) {
        const supcategories = [];
        category.supcategories?.forEach(supcategory => {
          if (this.categoryService.isDisplayed(supcategory)) {
            supcategories.push(supcategory);
          }
        });
        filteredCategories.push({
          ...category,
          supcategories
        });
      }
    });
    this.currentFilteredCategories = filteredCategories;
  }

  prepareCertificationName(): string {
    return this.productClassifications[0]?.attributes?.name;
  }

  prepareSolutionTypesName(multiValue: boolean): string {
    return multiValue ? this.productClassifications[1]?.attributes?.multipleValueName :
      this.productClassifications[1]?.attributes?.name;
  }

  prepareWorksWithName(multiValue: boolean): string {
    return multiValue ? this.productClassifications[2]?.attributes?.multipleValueName :
      this.productClassifications[2]?.attributes?.name;
  }

  /**
   * Data for the classification section
   */
  prepareFeatures() {
    this.classifications = [];
    this.sapCertifications = [];
    this.solutionTypes = [];
    this.worksWith = [];
    if (!this.product.classifications) {
      return;
    }
    this.features = this.product.classifications[0].features;

    for (const productClassification of this.productClassifications) {
      this.classifications.push(this.features.find((f) => f.code?.includes(productClassification.classification)));
    }

    if (this.classifications[0]) {
      this.classifications[0].featureValues.forEach((value) => {
        this.sapCertifications.push(value);
      });
      this.sapCertified = this.classifications[0].featureValues[0];
    }
    if (this.classifications[1]) {
      this.classifications[1].featureValues.forEach((value) => {
        this.solutionTypes.push(value.value);
      });
    }
    if (this.classifications[2]) {
      this.classifications[2].featureValues.forEach((value) => {
        this.worksWith.push(value.value);
      });
    }
  }

  addTags() {
    const metaKeywords = this.productUtilitiesService.getMetaKeywords(this.innerProduct);
    if (metaKeywords) {
      this.metaService.addTag({name: META_TAG_KEYWORDS, content: metaKeywords});
    }
  }

  prepareFeaturesAndAddTags() {
    if (this.product && this.productClassifications) {
      this.prepareFeatures();
      this.addTags();
    }
  }

  displayCategory(event: Event, categoryId: string) {
    if (isPlatformBrowser(this.platformId)) {
      event.preventDefault();
      this.categoryService.displayProductsOfCategory(categoryId);
    }
  }

  displayCategoryByKeyboard(event: KeyboardEvent, categoryId: string) {
    if (isPlatformBrowser(this.platformId) && (event.key === 'Enter' || event.key === 'NumpadEnter')) {
      this.displayCategory(event, categoryId);
    }
  }

  closeDsModal(modalConfig: any) {
    if (isPlatformBrowser(this.platformId)) {
      modalConfig.isActive = false;
    }
  }

  openDsModal(modalConfig: any): void {
    if (isPlatformBrowser(this.platformId)) {
      modalConfig.isActive = true;
    }
  }
}
